import { useCallback, useEffect, useState } from "react";

import { Helmet } from "react-helmet";
import toast from "react-hot-toast";

import { styled, useTheme } from "styled-components";

import SearchParams from "simplydo/core/searchParams";
import { OpenAPI } from "simplydo/interfaces";
import { Container } from "simplydo/react/ui";

import actions from "actions";
import api from "api";
import { useAppDispatch, useAppSelector } from "store";

import { HandledIdeaPreview } from "components/lib/Ideas/IdeaPreview";
import { Sticky } from "components/lib/UI";

import ActivityFeed from "./ActivityFeed";
import AssessmentAssignments from "./AssessmentAssignments";
import Challenges from "./Challenges";
import {
  HomeContentColumn as ContentColumn,
  HomeSidebar as Sidebar,
} from "./Containers";
import CuratedChallenges from "./CuratedChallenges";
import DownloadAppBanner from "./DownloadAppBanner";
import Header from "./Header";
import HomeGroups from "./HomeGroups";
import Ideas from "./Ideas";
import HomeInnovationIntelligence from "./InnovationIntelligence";
import Invitations from "./Invitations";
import Pages from "./Pages";
import Polls from "./Polls";
import ProjectAssignedIdeas from "./ProjectAssignedIdeas";
import SecurityRecommendations from "./SecurityRecommendations";
import HomeSuggestions from "./Suggestions";
import Tasklist from "./Tasklist";
import User from "./User";
import type { HomeDashboardState, HomeInvitation } from "./types";

const HomeColumn = styled.div<{ $fluid?: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: ${({ $fluid }) => ($fluid ? "0 0 10px" : "10px")};
  align-self: stretch;
  flex: 1;
  align-self: stretch;
`;

const HomeColumnContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: ${({ theme }) => (!theme.sizes.isComputer ? "0" : "25px 0")};
  flex: ${({ theme }) => (!theme.sizes.isComputer ? "1" : "1 1 0")};
  min-height: ${({ theme }) => (!theme.sizes.isComputer ? "100%" : null)};
  margin: ${({ theme }) => (!theme.sizes.isComputer ? "" : "0 0 0 30px;")};
  align-self: stretch;
`;

type IsMobileSidebarProps = { isComputer: boolean; children: React.ReactNode };
const IsMobileSidebar = ({ isComputer, children }: IsMobileSidebarProps) => {
  if (!isComputer) {
    return null;
  }
  return (
    <Sticky top={50} style={{ flex: "0.2 1 20%" }}>
      <Sidebar $top={50}>{children}</Sidebar>
    </Sticky>
  );
};

function Home() {
  const user = useAppSelector((state) => state.user);
  const dispatch = useAppDispatch();
  const onMessageThreadSelected = useCallback(
    (thread: OpenAPI.Schemas["MessageThread"], isPhone: boolean) => {
      dispatch(actions.messages.selectThread(thread, isPhone));
    },
    [dispatch],
  );

  // Example of the structure received from the API
  const [dashboardState, setDashboardState] = useState<HomeDashboardState>({
    ideas: [],
    challenges: [],
    invitations: [],
    projectAssignments: [],
    assessmentAssignments: [],
    polls: [],
    pinnedChallenges: [],
    groups: [],
  });
  const [ideaPreviewId, setIdeaPreviewId] = useState("");
  const theme = useTheme();
  const { isMobile } = theme.sizes;
  const hasAcceptedInvitation = user?.hasAcceptedInvitation;

  const getDashboard = useCallback(() => {
    api.users.dashboard(
      (data) => {
        setDashboardState(data);
      },
      (err) => {
        toast.error(err.message);
      },
    );
  }, []);

  useEffect(() => {
    if (hasAcceptedInvitation) {
      getDashboard();
    }
  }, [hasAcceptedInvitation, getDashboard]);

  const updateInvitations = useCallback(
    (updatedInvitations: HomeInvitation[]) => {
      setDashboardState((prevState) => ({
        ...prevState,
        invitations: updatedInvitations,
      }));
    },
    [],
  );

  const ownerOrgId = user?.ownerOrganisation?._id;

  useEffect(() => {
    getDashboard();
  }, [getDashboard, ownerOrgId]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    const params = new SearchParams(window.location.search);
    const thread = params.get("openThread");
    if (thread) {
      api.messages.getMessageThread(
        thread,
        (threadObject) => {
          onMessageThreadSelected(threadObject, isMobile);
        },
        () => {},
      );
    }
  }, [onMessageThreadSelected, isMobile]);

  if (!user) {
    return null;
  }
  return (
    <div>
      <Helmet title={"Home"} />
      <Container
        fluid
        style={{
          display: "flex",
          flexDirection: !theme.sizes.isComputer ? "column" : "row",
          flex: 1,
          paddingTop: !theme.sizes.isComputer ? "1rem" : null,
        }}
      >
        {/* HandledIdeaPreview supplies defaults for optional preview props */}
        <HandledIdeaPreview
          ideaId={ideaPreviewId || null}
          onCloseModal={() => setIdeaPreviewId("")}
          redirectToBoard={true}
        />
        <IsMobileSidebar isComputer={theme.sizes.isComputer}>
          <User />
          <Challenges challenges={dashboardState.challenges ?? []} />
          <Ideas ideas={dashboardState.ideas ?? []} />
        </IsMobileSidebar>
        <HomeColumnContainer>
          <DownloadAppBanner padded />
          <HomeColumn $fluid>
            <Header compact />
            <Invitations
              invitations={
                (dashboardState.invitations ?? []) as HomeInvitation[]
              }
              updateInvitations={updateInvitations}
              limit={3}
              showOnlyAcceptable
            />
            <HomeSuggestions />
            <Tasklist />
            <SecurityRecommendations />
            <ActivityFeed />
          </HomeColumn>
        </HomeColumnContainer>
        <ContentColumn>
          <HomeInnovationIntelligence />
          <AssessmentAssignments
            challenges={dashboardState.assessmentAssignments ?? []}
          />
          <ProjectAssignedIdeas
            assignments={dashboardState.projectAssignments ?? []}
            setIdeaPreviewId={setIdeaPreviewId}
          />
          <Polls polls={dashboardState.polls ?? []} />
          <CuratedChallenges
            challenges={dashboardState.pinnedChallenges ?? []}
          />
          <HomeGroups groups={dashboardState.groups ?? []} />
          <Pages />
        </ContentColumn>
      </Container>
    </div>
  );
}

export default Home;
